import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { X, Plus, Building2 } from "lucide-react";

const CATEGORIES = ["Programación", "Diseño", "Redacción", "Marketing", "Video & Audio", "IA & Datos"];

export default function CreateProject() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Programación",
    budget: "",
    deadline_days: "14",
  });
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("__custom");
  const [companyCustom, setCompanyCustom] = useState("");
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: "", description: "" });

  const loadCompanies = async () => {
    try {
      const { data } = await api.get("/companies/mine");
      setCompanies(data);
      if (data.length > 0 && companyId === "__custom") setCompanyId(data[0].company_id);
    } catch {}
  };

  useEffect(() => { loadCompanies(); /* eslint-disable-next-line */ }, []);

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !skills.includes(s)) setSkills([...skills, s]);
    setSkillInput("");
  };

  const createInlineCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name.trim()) return;
    try {
      const { data } = await api.post("/companies", newCompany);
      toast.success("Empresa creada");
      setCompanyDialogOpen(false);
      setNewCompany({ name: "", description: "" });
      await loadCompanies();
      setCompanyId(data.company_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        budget: parseFloat(form.budget),
        skills,
        deadline_days: parseInt(form.deadline_days, 10),
      };
      if (companyId && companyId !== "__custom") {
        payload.company_id = companyId;
      } else if (companyCustom.trim()) {
        payload.company_name = companyCustom.trim();
      }
      const { data } = await api.post("/projects", payload);
      toast.success("Proyecto publicado");
      navigate(`/projects/${data.project_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al publicar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="mb-10">
          <div className="text-xs tracking-widest uppercase text-primary font-bold mb-2">Nuevo proyecto</div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">Publica tu proyecto</h1>
          <p className="text-muted-foreground mt-3">Elige la empresa a nombre de la cual publicas, describe el trabajo y define el presupuesto.</p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          {/* Company selector */}
          <div>
            <Label className="text-xs tracking-widest uppercase font-semibold">Publicar a nombre de</Label>
            <div className="flex gap-2 mt-1.5">
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger className="h-11 flex-1" data-testid="new-project-company-select">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 opacity-70" />
                    <SelectValue placeholder="Elige una empresa" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.company_id} value={c.company_id} data-testid={`company-option-${c.company_id}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__custom">Otra (escribir manualmente)</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={() => setCompanyDialogOpen(true)} className="rounded-md h-11 px-4" data-testid="add-company-inline-btn">
                <Plus className="w-4 h-4 mr-1" /> Nueva
              </Button>
            </div>
            {companyId === "__custom" && (
              <Input value={companyCustom} onChange={(e) => setCompanyCustom(e.target.value)}
                placeholder="Ej. Acme Corp"
                className="mt-2 h-11" data-testid="new-project-company-name" />
            )}
          </div>

          <div>
            <Label htmlFor="title" className="text-xs tracking-widest uppercase font-semibold">Título</Label>
            <Input id="title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ej. Diseñar logo para startup fintech"
              className="mt-1.5 h-11" data-testid="new-project-title" />
          </div>

          <div>
            <Label htmlFor="desc" className="text-xs tracking-widest uppercase font-semibold">Descripción</Label>
            <Textarea id="desc" required rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe detalladamente lo que necesitas…"
              className="mt-1.5" data-testid="new-project-description" />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs tracking-widest uppercase font-semibold">Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="new-project-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="budget" className="text-xs tracking-widest uppercase font-semibold">Presupuesto (USD)</Label>
              <Input id="budget" type="number" min="10" required value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
                placeholder="80" className="mt-1.5 h-11" data-testid="new-project-budget" />
            </div>
            <div>
              <Label htmlFor="days" className="text-xs tracking-widest uppercase font-semibold">Plazo (días)</Label>
              <Input id="days" type="number" min="1" required value={form.deadline_days} onChange={(e) => setForm({ ...form, deadline_days: e.target.value })}
                className="mt-1.5 h-11" data-testid="new-project-deadline" />
            </div>
          </div>

          <div>
            <Label className="text-xs tracking-widest uppercase font-semibold">Habilidades requeridas</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={skillInput} onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
                placeholder="Ej. React, Figma…" className="h-11" data-testid="skill-input" />
              <Button type="button" onClick={addSkill} variant="outline" className="rounded-md" data-testid="add-skill-btn">
                Añadir
              </Button>
            </div>
            {skills.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {skills.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 text-sm px-3 py-1 rounded-full bg-accent text-accent-foreground">
                    {s}
                    <button type="button" onClick={() => setSkills(skills.filter(x => x !== s))} aria-label={`Quitar ${s}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 flex gap-3">
            <Button type="submit" disabled={loading} className="rounded-full h-11 px-8" data-testid="publish-project-btn">
              {loading ? "Publicando…" : "Publicar proyecto"}
            </Button>
            <Button type="button" variant="outline" className="rounded-full h-11 px-6" onClick={() => navigate(-1)}>
              Cancelar
            </Button>
          </div>
        </form>
      </div>

      {/* Inline new company dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva empresa</DialogTitle>
            <DialogDescription>Se guardará en tu perfil y podrás reutilizarla en próximos proyectos.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createInlineCompany} className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase font-semibold">Nombre *</Label>
              <Input value={newCompany.name} onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                placeholder="Acme Corp" required className="mt-1.5 h-11" data-testid="inline-company-name" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase font-semibold">Descripción</Label>
              <Textarea rows={3} value={newCompany.description} onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                placeholder="Qué hace la empresa…" className="mt-1.5" data-testid="inline-company-description" />
            </div>
            <Button type="submit" className="rounded-full w-full h-11" data-testid="save-inline-company-btn">
              Crear empresa
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
